// client/src/components/IssueTriage/ElementPicker.tsx
import { useState, useEffect } from 'react';
import { X, Target } from 'lucide-react';
import type { SelectedElement } from '../../types/issue-triage';

interface ElementPickerProps {
  isActive: boolean;
  onSelect: (element: SelectedElement) => void;
  onCancel: () => void;
}

/**
 * ElementPicker - Overlay that lets users click on page elements to select them
 *
 * When active, shows a highlight box around hovered elements and captures
 * element details on click.
 */
export function ElementPicker({ isActive, onSelect, onCancel }: ElementPickerProps) {
  const [highlightBox, setHighlightBox] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isActive) {
      setHighlightBox(null);
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Ignore picker UI elements
      if (target.closest('[data-element-picker]')) return;

      setHighlightBox(target.getBoundingClientRect());
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Ignore picker UI elements
      if (target.closest('[data-element-picker]')) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = target.getBoundingClientRect();
      const selected: SelectedElement = {
        selector: getSelector(target),
        tagName: target.tagName.toLowerCase(),
        className: typeof target.className === 'string' ? target.className : '',
        textContent: (target.textContent || '').slice(0, 200).trim(),
        boundingRect: {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
        },
      };

      onSelect(selected);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown);

    // Add cursor style
    document.body.style.cursor = 'crosshair';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.cursor = '';
    };
  }, [isActive, onSelect, onCancel]);

  if (!isActive) return null;

  return (
    <>
      {/* Highlight overlay */}
      {highlightBox && (
        <div
          data-element-picker="highlight"
          className="pointer-events-none fixed z-[9998] border-2 border-blue-500 bg-blue-500/10 transition-all duration-75"
          style={{
            left: highlightBox.x,
            top: highlightBox.y,
            width: highlightBox.width,
            height: highlightBox.height,
          }}
        />
      )}

      {/* Instruction bar */}
      <div
        data-element-picker="instructions"
        className="fixed left-1/2 top-4 z-[9999] -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white shadow-lg"
      >
        <div className="flex items-center gap-3">
          <Target className="h-4 w-4 text-blue-400" />
          <span>Click on the element you want to report</span>
          <span className="text-slate-400">|</span>
          <span className="text-slate-400 text-xs">Press ESC to cancel</span>
          <button
            onClick={onCancel}
            className="ml-2 rounded p-1 hover:bg-slate-700"
            aria-label="Cancel element picker"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Generate a CSS selector for an element
 */
function getSelector(element: HTMLElement): string {
  // Priority 1: ID
  if (element.id) {
    return `#${element.id}`;
  }

  // Priority 2: data-testid
  if (element.getAttribute('data-testid')) {
    return `[data-testid="${element.getAttribute('data-testid')}"]`;
  }

  // Priority 3: tag + classes
  const tag = element.tagName.toLowerCase();
  const classes =
    typeof element.className === 'string' && element.className
      ? `.${element.className.split(' ').filter(Boolean).slice(0, 3).join('.')}`
      : '';

  // Priority 4: Add nth-child for uniqueness
  let selector = `${tag}${classes}`;

  // Try to make it more specific with parent context
  const parent = element.parentElement;
  if (parent && parent !== document.body) {
    if (parent.id) {
      selector = `#${parent.id} > ${selector}`;
    } else if (parent.getAttribute('data-testid')) {
      selector = `[data-testid="${parent.getAttribute('data-testid')}"] > ${selector}`;
    }
  }

  return selector.slice(0, 150);
}
